import { useI18n } from '@ecom/i18n';
import { Button, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { variantFormSchema, type VariantFormValues } from '../schemas';

import { whenDefined } from '@/lib/optional-props';

/**
 * React Hook Form's typed `Path<VariantFormValues>` requires a literal
 * `attributes.${number}.key` template — not `${string}` — so `index` must
 * stay a `number` in this one template, which is exactly what
 * `restrict-template-expressions` normally forbids. Isolated here, in the
 * one place that genuinely needs it, rather than disabled ad hoc at each
 * call site.
 */
function attributePath(
  index: number,
  field: 'key' | 'value',
): `attributes.${number}.${'key' | 'value'}` {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- see comment above.
  return `attributes.${index}.${field}`;
}

export function VariantForm({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (values: VariantFormValues) => void;
  isSubmitting: boolean;
}): ReactElement {
  const { t } = useI18n();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VariantFormValues>({
    resolver: zodResolver(variantFormSchema),
    defaultValues: { sku: '', attributes: [{ key: '', value: '' }] },
  });

  const attributeFields = useFieldArray({ control, name: 'attributes' });

  return (
    <form
      id="variant-form"
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <fieldset disabled={isSubmitting} className="contents">
        <Field
          label={t('merchant.products.variants.skuLabel')}
          {...whenDefined('error', errors.sku?.message)}
        >
          {(bind) => <Input {...bind} {...register('sku')} autoFocus />}
        </Field>

        <Field
          label={t('merchant.products.variants.colSpecificPrice')}
          optional
          hint={t('merchant.products.variants.priceOverrideHint')}
          {...whenDefined('error', errors.priceOverride?.message)}
        >
          {(bind) => (
            <Input
              {...bind}
              type="number"
              step="0.001"
              min="0"
              {...register('priceOverride', { valueAsNumber: true })}
            />
          )}
        </Field>

        <div>
          <p className="mb-2 text-sm font-medium text-ink">
            {t('merchant.products.variants.colAttributes')}{' '}
            <span className="text-inkFaint">
              {t('merchant.products.variants.attributesExample')}
            </span>
          </p>
          <div className="flex flex-col gap-2">
            {attributeFields.fields.map((field, index) => (
              <div key={field.id} className="flex items-center gap-2">
                <Input
                  placeholder={t('merchant.products.variants.attributeKeyPlaceholder')}
                  {...register(attributePath(index, 'key'))}
                />
                <Input
                  placeholder={t('merchant.products.variants.attributeValuePlaceholder')}
                  {...register(attributePath(index, 'value'))}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    attributeFields.remove(index);
                  }}
                >
                  {t('merchant.products.variants.removeAttribute')}
                </Button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => {
              attributeFields.append({ key: '', value: '' });
            }}
          >
            {t('merchant.products.variants.addAttribute')}
          </Button>
        </div>
      </fieldset>
    </form>
  );
}
